/**
 * Derive the Employment Review cockpit's single next action from the canonical
 * backend snapshot. The backend owns blocker order through summary.items; this
 * helper only translates the first blocking canonical item into a UI action key.
 *
 * Keeping the order here data-driven prevents the cockpit CTA from disagreeing
 * with the five canonical cards (for example showing "Open application forms"
 * when the application is actually recoverable by an admin).
 */
export function deriveEmploymentReviewNextAction(snapshot, { signedOff = false } = {}) {
  if (!snapshot || signedOff || snapshot?.sign_off?.ready) return null;

  const items = Array.isArray(snapshot?.summary?.items) ? snapshot.summary.items : [];
  const firstBlocking = items.find((item) => item?.blocking);
  if (!firstBlocking?.key) return { key: 'review_blockers', owner: 'admin' };

  // New snapshots own both routing fields. Keep the switch below only as a
  // compatibility fallback for a browser briefly talking to an older backend.
  if (firstBlocking.next_action) {
    return {
      key: firstBlocking.next_action,
      owner: firstBlocking.owner || 'admin',
    };
  }

  switch (firstBlocking.key) {
    case 'application': {
      const application = snapshot.application || {};
      if (application.admin_action === 'recover_application') {
        return { key: 'recover_application', owner: 'admin' };
      }
      if (application.admin_action === 'review_possible_historical_application') {
        return { key: 'review_possible_historical_application', owner: 'admin' };
      }
      if (application.applicant_action === 'confirm_recovered_application') {
        return { key: 'confirm_recovered_application', owner: 'applicant' };
      }
      return { key: 'complete_application', owner: 'applicant' };
    }

    case 'work_history':
      return { key: 'add_work_history', owner: 'applicant' };

    case 'gaps': {
      const gaps = snapshot.gaps || {};
      if (gaps.applicant_action === 'explain_or_revise_employment_gaps') {
        return { key: 'explain_or_revise_employment_gaps', owner: 'applicant' };
      }
      if (gaps.admin_action === 'review_employment_gaps') {
        return { key: 'review_employment_gaps', owner: 'admin' };
      }
      return { key: 'review_employment_gaps', owner: 'admin' };
    }

    case 'cv': {
      const cv = snapshot.cv || {};
      if (cv.admin_action === 'recover_cv') {
        return { key: 'recover_cv', owner: 'admin' };
      }
      if (cv.admin_action === 'review_cv') {
        return { key: 'review_cv', owner: 'admin' };
      }
      if (cv.admin_action === 'request_cv_replacement') {
        return { key: 'request_cv_replacement', owner: 'admin' };
      }
      if (cv.admin_action === 'request_cv_upload') {
        return { key: 'request_cv_upload', owner: 'admin' };
      }
      if (cv.state === 'replacement_required') {
        return { key: 'wait_for_cv_replacement', owner: 'applicant' };
      }
      return { key: 'review_cv_blocker', owner: 'admin' };
    }

    case 'declarations': {
      const declarations = snapshot.declarations || {};
      if (declarations.on_file && !declarations.admin_reviewed) {
        return { key: 'review_declarations', owner: 'admin' };
      }
      return { key: 'complete_declarations', owner: 'applicant' };
    }

    default:
      return { key: 'review_blockers', owner: 'admin' };
  }
}
